import generatePicker, {
	PickerProps,
	RangePickerProps as RangePickerPropsUi,
} from "antd/lib/date-picker/generatePicker";
import { Dayjs } from "dayjs";
import dayJsGenerateConfig from "rc-picker/lib/generate/dayjs";
import { RangeValue as RangeValueRcPicker } from "rc-picker/lib/interface";
import * as React from "react";

import "./date-picker.global.scss";

export type DatePickerAbsProps = PickerProps<Dayjs>;

export type RangeValue<DateType> = RangeValueRcPicker<DateType>;

export type DateFilterType = "competence" | "liquidation";

export type RangePickerProps = RangePickerPropsUi<Dayjs> & {
	shouldAdjustRange?: boolean;
	resetInitialValueOnOpen?: boolean;
	showDateTypeSelector?: boolean;
	dateType?: DateFilterType;
	onDateTypeChange?: (type: DateFilterType) => void;
	dateTypeSelectorLabels?: {
		label: string;
		competence: string;
		liquidation: string;
		apply: string;
	};
};

const CustomDatePicker = generatePicker<Dayjs>(dayJsGenerateConfig);

export function DatePicker({ ...props }: DatePickerAbsProps) {
	return <CustomDatePicker {...props} />;
}

function RangePicker({
	value,
	onChange,
	onCalendarChange: onCalendarChangeProp,
	shouldAdjustRange = true,
	resetInitialValueOnOpen = false,
	showDateTypeSelector = false,
	dateType = "competence",
	onDateTypeChange,
	dateTypeSelectorLabels = {
		label: "Filtrar por",
		competence: "Competência",
		liquidation: "Liquidação",
		apply: "Aplicar",
	},
	...props
}: RangePickerProps) {
	const [dates, setDates] = React.useState<[Dayjs | null, Dayjs | null]>(
		value ?? [null, null],
	);
	const [pickerOpen, setPickerOpen] = React.useState(false);
	const [localDateType, setLocalDateType] = React.useState<DateFilterType>(dateType);
	const preventAutoCloseRef = React.useRef(false);

	React.useEffect(() => {
		if (value !== undefined) {
			setDates(value ?? [null, null]);
		}
	}, [value]);

	React.useEffect(() => {
		setLocalDateType(dateType);
	}, [dateType]);

	function normalizeRange(range: [Dayjs, Dayjs]): [Dayjs, Dayjs] {
		if (!shouldAdjustRange) return range;

		return [range[0].startOf("day"), range[1].endOf("day")];
	}

	const handleApply = React.useCallback(() => {
		if (onDateTypeChange && localDateType !== dateType) {
			onDateTypeChange(localDateType);
		}

		if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
			preventAutoCloseRef.current = false;
			setPickerOpen(false);
			return;
		}

		const normalized = normalizeRange(dates as [Dayjs, Dayjs]);

		onChange?.(normalized, [
			normalized[0].format("YYYY-MM-DD"),
			normalized[1].format("YYYY-MM-DD"),
		]);

		preventAutoCloseRef.current = false;
		setPickerOpen(false);
	}, [dates, localDateType, dateType, onDateTypeChange, onChange]);

	const renderExtraFooter = React.useCallback(() => {
		if (!showDateTypeSelector) return null;

		return (
			<div className="datePickerTypeSelector">
				<div className="datePickerTypeLeft">
					<span className="datePickerTypeLabel">{dateTypeSelectorLabels.label}</span>
					<div className="datePickerTypeButtons">
						<button
							type="button"
							className={`datePickerTypeButton ${
								localDateType === "competence" ? "active" : ""
							}`}
							onClick={() => setLocalDateType("competence")}
						>
							{dateTypeSelectorLabels.competence}
						</button>
						<button
							type="button"
							className={`datePickerTypeButton ${
								localDateType === "liquidation" ? "active" : ""
							}`}
							onClick={() => setLocalDateType("liquidation")}
						>
							{dateTypeSelectorLabels.liquidation}
						</button>
					</div>
				</div>
				<button type="button" className="datePickerTypeApply" onClick={handleApply}>
					{dateTypeSelectorLabels.apply}
				</button>
			</div>
		);
	}, [showDateTypeSelector, handleApply, localDateType, dateTypeSelectorLabels]);

	return (
		<CustomDatePicker.RangePicker
			value={dates}
			open={showDateTypeSelector ? pickerOpen : undefined}
			onOpenChange={open => {
				if (showDateTypeSelector) {
					if (!open && preventAutoCloseRef.current) {
						preventAutoCloseRef.current = false;
						return;
					}
					if (open) {
						preventAutoCloseRef.current = false;
					}
					setPickerOpen(open);
				}
				if (open && resetInitialValueOnOpen) {
					return setDates([null, null]);
				}
			}}
			onCalendarChange={(period, formatString, info) => {
				if (showDateTypeSelector) {
					if (!period) {
						setDates([null, null]);
						preventAutoCloseRef.current = false;
						return;
					}

					if (period.length !== 2 || !period.every(Boolean)) {
						setDates(period);
						preventAutoCloseRef.current = false;
						return;
					}

					setDates(period);
					preventAutoCloseRef.current = true;
					return;
				}

				const changeFn = onCalendarChangeProp ?? onChange;

				if (!period) {
					setDates([null, null]);
					if (!changeFn) return;
					changeFn(null, formatString, info);
					return;
				}

				// eslint-disable-next-line @typescript-eslint/no-magic-numbers
				if (period.length !== 2 || !period.every(Boolean)) return;

				setDates(period);

				if (!changeFn) return;

				const normalized = normalizeRange(period as [Dayjs, Dayjs]);

				changeFn(normalized, formatString, info);
			}}
			renderExtraFooter={renderExtraFooter}
			{...props}
		/>
	);
}

DatePicker.RangePicker = RangePicker;
DatePicker.TimePicker = CustomDatePicker.TimePicker;
DatePicker.WeekPicker = CustomDatePicker.WeekPicker;
DatePicker.MonthPicker = CustomDatePicker.MonthPicker;
DatePicker.QuarterPicker = CustomDatePicker.QuarterPicker;
DatePicker.YearPicker = CustomDatePicker.YearPicker;
